import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  doc, onSnapshot, collection, query, orderBy,
  addDoc, serverTimestamp, setDoc, Timestamp, updateDoc, getDocs,
} from 'firebase/firestore';
import { ref, getDownloadURL, uploadBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { QRCodeSVG } from 'qrcode.react';
import { db, storage, functions } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { usePreviewNavigate } from '../../contexts/PreviewContext';
import {
  CATEGORY_LABELS, CATEGORY_STYLES, CATEGORY_DOT,
  isCategoryComplete, CATEGORY_STATUS_SYNC,
} from '../../utils/statuses';
import useStatusPipeline from '../../hooks/useStatusPipeline';
import { useExpenseCategories } from '../../utils/expenseCategories';
import FullItineraryBuilder from '../../components/FullItineraryBuilder';
import TeamInfoEditPanel from '../../components/TeamInfoEditPanel';
import FoodOrders from './FoodOrders';
import StaffRoomingTab from '../../components/StaffRoomingTab';
import TeamsTab from '../../components/TeamsTab';
import TeamFixturesSection from '../../components/TeamFixtures';
import SquadCard from '../../components/SquadCard';
import SocialMediaPlan from '../../components/SocialMediaPlan';
import GameContentChecklist from '../../components/GameContentChecklist';
import ResourcePack from '../../components/ResourcePack';
import TourNotes from '../../components/TourNotes';
import { totalTravellers } from '../../utils/groupSize';

const TABS = [
  { key: 'tour',         label: 'Tour Info' },
  { key: 'itinerary',   label: 'Itinerary' },
  { key: 'team_info',   label: 'Team Info' },
  { key: 'participants', label: 'Participants' },
  { key: 'register',    label: 'Register' },
  { key: 'social',      label: 'Social Media' },
  { key: 'checklist',   label: 'Checklist' },
  { key: 'expenses',    label: 'Expenses' },
  { key: 'rooming',     label: 'Rooming' },
  { key: 'teams',       label: 'Teams' },
  { key: 'incidents',   label: 'Incidents' },
  { key: 'food',        label: 'Food' },
  { key: 'resources',   label: 'Resources' },
];

const PRIMARY_TABS = TABS.slice(0, 5);
const MORE_TABS    = TABS.slice(5);

const INCIDENT_TYPES = ['Injury', 'Illness', 'Lost Property', 'Misconduct', 'Accident', 'Other'];


function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-0.5">{label}</p>
      <p className="text-[13px] text-[#111827]">{value || '—'}</p>
    </div>
  );
}


// ── Participants (read-only, all fields including medical) ────────────────────

function fmtPDate(str) {
  if (!str) return null;
  const parts = str.split('-');
  if (parts.length !== 3) return str;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function PField({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-[0.8px]">{label}</p>
      <p className="text-[12px] text-[#111827] mt-0.5 leading-snug">{value}</p>
    </div>
  );
}

function ParticipantsView({ requestId }) {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [expandedId, setExpandedId]     = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'requests', requestId, 'participants')),
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() ?? a.submittedAt?.toMillis?.() ?? 0;
          const tb = b.createdAt?.toMillis?.() ?? b.submittedAt?.toMillis?.() ?? 0;
          return ta - tb;
        });
        setParticipants(docs);
        setLoading(false);
      }
    );
    return unsub;
  }, [requestId]);

  if (loading) return <p className="text-[13px] text-[#9CA3AF]">Loading…</p>;
  if (participants.length === 0) {
    return <p className="text-[13px] text-[#9CA3AF]">No participant details submitted yet.</p>;
  }

  function fullName(p) {
    return [p.firstName, p.middleNames, p.surname].filter(Boolean).join(' ') || '—';
  }

  const NO_REQ_VALUES = new Set(['none', 'n/a', 'na', 'no', 'nil', 'n', '-', 'not applicable', 'not applicable.', 'no allergies', 'no dietary requirements', 'none.', 'no.', 'n/a.']);
  const hasReq = (v) => !!v && !NO_REQ_VALUES.has(v.trim().toLowerCase());
  const dietary = participants.filter((p) => hasReq(p.allergies) || hasReq(p.dietaryRequirements) || hasReq(p.conditions) || hasReq(p.safetyInfo));

  return (
    <div className="space-y-4">
      {dietary.length > 0 && (
        <div className="bg-amber-light border border-ba/25 rounded-[12px] overflow-hidden">
          <div className="px-4 py-3 border-b border-ba/20">
            <p className="text-[11px] font-bold text-[#92400E] uppercase tracking-[0.9px]">Dietary / Medical Requirements</p>
            <p className="text-[12px] text-[#92400E]/80 mt-0.5">{dietary.length} participant{dietary.length !== 1 ? 's' : ''} with requirements</p>
          </div>
          <div className="divide-y divide-ba/10">
            {dietary.map((p) => (
              <div key={p.id} className="px-4 py-3">
                <p className="text-[13px] font-semibold text-[#111827]">{fullName(p)}</p>
                {hasReq(p.allergies) && <p className="text-[12px] text-[#92400E] mt-0.5"><strong>Allergies:</strong> {p.allergies}</p>}
                {hasReq(p.dietaryRequirements) && <p className="text-[12px] text-[#374151] mt-0.5"><strong>Dietary:</strong> {p.dietaryRequirements}</p>}
                {hasReq(p.conditions) && <p className="text-[12px] text-[#374151] mt-0.5"><strong>Medical:</strong> {p.conditions}</p>}
                {hasReq(p.safetyInfo) && <p className="text-[12px] text-[#92400E] mt-0.5"><strong>Safety:</strong> {p.safetyInfo}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-2">
      {participants.map((p, i) => {
        const isExpanded = expandedId === p.id;
        return (
          <div key={p.id} className="bg-surface border border-[#E5E7EB] rounded-[10px] overflow-hidden">
            <button
              onClick={() => setExpandedId(isExpanded ? null : p.id)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-[#111827] truncate">{i + 1}. {fullName(p)}</p>
                <p className="text-[11px] text-[#6B7280] mt-0.5">
                  {[p.travellerType, p.llsCentre ? `Centre: ${p.llsCentre}` : null, p.dateOfBirth ? `DOB: ${fmtPDate(p.dateOfBirth)}` : null].filter(Boolean).join(' · ')}
                </p>
              </div>
              <svg className={`w-4 h-4 text-[#9CA3AF] flex-shrink-0 ml-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isExpanded && (
              <div className="px-4 pb-4 pt-3 border-t border-[#F3F4F6] space-y-4">
                <div>
                  <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-[0.9px] mb-2">Personal</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <PField label="Gender"        value={p.gender} />
                    <PField label="Date of Birth" value={fmtPDate(p.dateOfBirth)} />
                    <PField label="Phone"         value={p.contactPhone} />
                    <PField label="Email"         value={p.contactEmail} />
                  </div>
                </div>

                {(p.guardianName || p.guardianPhone || p.guardianEmail) && (
                  <div>
                    <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-[0.9px] mb-2">Guardian / Parent</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      <PField label="Name"  value={p.guardianName} />
                      <PField label="Phone" value={p.guardianPhone} />
                      <PField label="Email" value={p.guardianEmail} />
                      <PField label="Passport No." value={p.guardianPassportNumber} />
                      <PField label="Nationality"  value={p.guardianNationality} />
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-[0.9px] mb-2">Medical / Dietary</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <PField label="Allergies"          value={p.allergies} />
                    <PField label="Dietary"            value={p.dietaryRequirements} />
                    <PField label="Medical Conditions" value={p.conditions} />
                    <PField label="Safety Info"        value={p.safetyInfo} />
                    <PField label="Swimming Level"     value={p.swimmingLevel} />
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-[0.9px] mb-2">Passport / Travel</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <PField label="Passport No."      value={p.passportNumber} />
                    <PField label="Expiry"            value={fmtPDate(p.passportExpiry)} />
                    <PField label="Nationality"       value={p.nationality} />
                    <PField label="Country of Issue"  value={p.countryOfIssue} />
                  </div>
                </div>

                {p.formVariant === 'mic_cup' && (
                  <>
                    <div>
                      <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-[0.9px] mb-2">Playing Details</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <PField label="Shirt Number" value={p.shirtNumber} />
                        <PField label="Position"     value={p.position} />
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-[0.9px] mb-2">Travel Arrangements</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <PField label="Flight Arrangement"      value={p.flightArrangementMethod} />
                        <PField label="Travel Group Size"       value={p.travelGroupSize} />
                        <PField label="Outbound Flight Details" value={p.outboundFlightDetails} />
                        <PField label="Outbound Flight Number"  value={p.outboundFlightNumber} />
                        <PField label="Return Flight Details"   value={p.returnFlightDetails} />
                        <PField label="Return Flight Number"    value={p.returnFlightNumber} />
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-[0.9px] mb-2">Insurance</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <PField label="Declaration Confirmed" value={p.insuranceConfirmed ? 'Yes' : 'No'} />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const SCAN_URL_BASE = 'https://mst-sports-tours.web.app/scan';
const SESSION_TTL_MS = 10 * 60 * 1000;

function isMobileDevice() {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

// ── QR Scan Panel (desktop) ───────────────────────────────────────────────────

function QRScanPanel({ currentUser, onScanned, onCancel }) {
  const [qrUrl,    setQrUrl]    = useState(null);
  const [phase,    setPhase]    = useState('generating');
  const [secsLeft, setSecsLeft] = useState(600);
  const unsubRef  = useRef(null);
  const timerRef  = useRef(null);

  const teardown = useCallback(() => {
    unsubRef.current?.();
    clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    async function createSession() {
      const t = crypto.randomUUID();
      await setDoc(doc(db, 'scanSessions', t), {
        status:    'pending',
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(new Date(Date.now() + SESSION_TTL_MS)),
      });
      setQrUrl(`${SCAN_URL_BASE}/${t}`);
      setPhase('waiting');

      timerRef.current = setInterval(() => {
        setSecsLeft((s) => {
          if (s <= 1) { clearInterval(timerRef.current); setPhase('error'); return 0; }
          return s - 1;
        });
      }, 1000);

      unsubRef.current = onSnapshot(doc(db, 'scanSessions', t), async (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        if (data.status !== 'uploaded') return;
        teardown();
        setPhase('scanning');
        try {
          let downloadUrl = null;
          try { downloadUrl = await getDownloadURL(ref(storage, data.storagePath)); } catch {}
          const scanFn = httpsCallable(functions, 'scanReceipt');
          const result = await scanFn({ storagePath: data.storagePath });
          onScanned(result.data, downloadUrl);
        } catch {
          setPhase('error');
        }
      });
    }
    createSession().catch(() => setPhase('error'));
    return teardown;
  }, [currentUser.uid, onScanned, teardown]);

  const mins = String(Math.floor(secsLeft / 60)).padStart(2, '0');
  const secs = String(secsLeft % 60).padStart(2, '0');

  return (
    <div className="p-5 bg-surface rounded-[10px] border border-[#E5E7EB] mb-4">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-[14px] font-semibold text-[#111827]">Scan Receipt with Phone</p>
          <p className="text-[12px] text-[#6B7280] mt-0.5">Point your phone camera at the QR code — no login needed.</p>
        </div>
        <button onClick={() => { teardown(); onCancel(); }} className="text-[12px] text-[#6B7280] hover:text-[#374151] ml-4 flex-shrink-0">Cancel</button>
      </div>

      {phase === 'generating' && (
        <div className="flex justify-center py-10">
          <svg className="w-6 h-6 animate-spin text-[#9CA3AF]" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      )}

      {phase === 'waiting' && qrUrl && (
        <div className="flex flex-col sm:flex-row items-center gap-8">
          <div className="p-3 bg-white border border-[#E5E7EB] rounded-[12px] flex-shrink-0">
            <QRCodeSVG value={qrUrl} size={160} level="M" />
          </div>
          <div className="flex-1">
            <ol className="space-y-3">
              {[
                'Open your phone camera and point it at the QR code',
                'Tap the link that appears — it opens in your browser',
                'Take a photo of the receipt or choose one from your photos',
                'Tap "Send" — the form fills in automatically here',
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-pb text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                  <span className="text-[13px] text-[#374151]">{step}</span>
                </li>
              ))}
            </ol>
            <div className="mt-4 flex items-center gap-2">
              <svg className="w-4 h-4 animate-spin text-[#9CA3AF]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <span className="text-[12px] text-[#9CA3AF]">Waiting for phone… expires in {mins}:{secs}</span>
            </div>
          </div>
        </div>
      )}

      {phase === 'scanning' && (
        <div className="flex items-center gap-3 py-6">
          <svg className="w-5 h-5 animate-spin text-pb flex-shrink-0" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <span className="text-[13px] text-[#374151]">Receipt received — scanning with AI…</span>
        </div>
      )}

      {phase === 'error' && (
        <div className="py-3">
          <p className="text-[13px] text-dr mb-2">
            {secsLeft === 0 ? 'QR code expired.' : 'Something went wrong scanning the receipt.'}
          </p>
          <button onClick={() => { teardown(); onCancel(); }} className="text-[13px] text-pb hover:text-navy font-medium">
            Close and try again
          </button>
        </div>
      )}
    </div>
  );
}

// ── Expenses (tour leader can log expenses) ───────────────────────────────────

const emptyExp = { date: '', amount: '', currency: 'EUR', category: '', description: '' };

function ExpensesView({ requestId, request }) {
  const { currentUser } = useAuth();
  const expenseCategories = useExpenseCategories();
  const [expenses, setExpenses]     = useState([]);
  const [showForm, setShowForm]     = useState(false);
  // scanMode: null | 'mobile' | 'qr'
  const [scanMode, setScanMode]     = useState(null);
  const [mobileScanPhase, setMobileScanPhase] = useState('idle'); // idle | uploading | scanning | error
  const [mobileScanError, setMobileScanError] = useState('');
  const [form, setForm]             = useState(emptyExp);
  const [receiptUrl, setReceiptUrl] = useState('');
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const fileInputRef                = useRef(null);

  function handleScanClick() {
    if (isMobileDevice()) {
      setScanMode('mobile');
      setMobileScanPhase('idle');
      setMobileScanError('');
      fileInputRef.current?.click();
    } else {
      setScanMode('qr');
    }
  }

  async function handleScanFile(e) {
    const file = e.target.files?.[0];
    if (!file) { setScanMode(null); return; }
    setMobileScanPhase('uploading');
    setMobileScanError('');
    try {
      const storagePath = `receipts/${currentUser.uid}/tl-${Date.now()}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      let downloadUrl = null;
      try { downloadUrl = await getDownloadURL(storageRef); } catch {}
      setMobileScanPhase('scanning');
      const scanFn = httpsCallable(functions, 'scanReceipt');
      const result = await scanFn({ storagePath });
      handleScanned(result.data, downloadUrl);
    } catch (err) {
      console.error('Scan error:', err);
      setMobileScanError(err?.message || 'Something went wrong scanning the receipt.');
      setMobileScanPhase('error');
    }
  }

  function handleScanned(data, downloadUrl) {
    data = data || {};
    setForm((p) => ({
      ...p,
      date:        data.date        || p.date,
      amount:      data.amount      != null ? String(data.amount) : p.amount,
      currency:    data.currency    || p.currency,
      category:    data.category    || p.category,
      description: data.description || p.description,
    }));
    if (downloadUrl) setReceiptUrl(downloadUrl);
    setScanMode(null);
    setMobileScanPhase('idle');
    setShowForm(true);
  }

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'expenses'), orderBy('date', 'desc')),
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setExpenses(all.filter((e) => e.requestId === requestId && !e.storedOnly));
      }
    );
    return unsub;
  }, [requestId]);

  async function handleAdd(e) {
    e.preventDefault();
    setError('');
    if (!form.date || !form.amount || !form.category || !form.description.trim()) {
      setError('All fields are required.'); return;
    }
    setSaving(true);
    try {
      await addDoc(collection(db, 'expenses'), {
        requestId,
        requestSummary: request.tourName || `${request.sport} — ${request.destination}`,
        date:          form.date,
        amount:        Number(form.amount),
        currency:      form.currency,
        category:      form.category,
        description:   form.description.trim(),
        paymentStatus: 'paid',
        receiptUrl:    receiptUrl || null,
        submittedBy:   currentUser.email,
        submittedRole: 'tour_leader',
        createdAt:     serverTimestamp(),
      });
      setForm(emptyExp);
      setReceiptUrl('');
      setShowForm(false);
    } catch (err) {
      console.error(err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const sym = { GBP: '£', EUR: '€', USD: '$' };
  const total = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);

  return (
    <div>
      {expenses.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">
              {expenses.length} expense{expenses.length !== 1 ? 's' : ''} logged
            </p>
            <p className="text-[13px] font-bold text-[#111827]">
              Total: {sym[expenses[0]?.currency] || '£'}{total.toFixed(2)}
            </p>
          </div>
          <div className="space-y-2">
            {expenses.map((exp) => (
              <div key={exp.id} className="flex items-start justify-between gap-3 p-3 bg-surface border border-[#E5E7EB] rounded-[10px]">
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-[#111827]">{exp.description}</p>
                  <p className="text-[12px] text-[#6B7280] mt-0.5">{exp.category} · {formatDate(exp.date)}</p>
                </div>
                <p className="text-[14px] font-bold text-[#111827] whitespace-nowrap flex-shrink-0">
                  {sym[exp.currency] || '£'}{Number(exp.amount).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {expenses.length === 0 && !showForm && (
        <p className="text-[13px] text-[#9CA3AF] mb-4">No expenses logged yet.</p>
      )}

      {/* Hidden file input — camera only on mobile */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleScanFile}
      />

      {/* Desktop: QR scan panel */}
      {scanMode === 'qr' && (
        <QRScanPanel
          currentUser={currentUser}
          onScanned={handleScanned}
          onCancel={() => setScanMode(null)}
        />
      )}

      {/* Mobile: upload progress */}
      {scanMode === 'mobile' && (mobileScanPhase === 'uploading' || mobileScanPhase === 'scanning') && (
        <div className="border border-[#E5E7EB] rounded-[10px] p-4 mb-4 bg-surface flex items-center gap-3">
          <svg className="w-5 h-5 animate-spin text-pb flex-shrink-0" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <span className="text-[13px] text-[#374151]">
            {mobileScanPhase === 'uploading' ? 'Uploading photo…' : 'Reading receipt with AI…'}
          </span>
        </div>
      )}

      {/* Mobile: scan error */}
      {scanMode === 'mobile' && mobileScanPhase === 'error' && (
        <div className="border border-[#E5E7EB] rounded-[10px] p-4 mb-4 bg-surface">
          <p className="text-[13px] text-dr mb-2">{mobileScanError || 'Could not read the receipt.'}</p>
          <button onClick={() => { setScanMode(null); setMobileScanPhase('idle'); }}
            className="text-[13px] text-pb hover:text-navy font-medium">Dismiss</button>
        </div>
      )}

      {showForm ? (
        <form onSubmit={handleAdd} className="border border-[#E5E7EB] rounded-[10px] p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-[12px] font-semibold text-[#374151]">Log an expense</p>
            {receiptUrl && (
              <a href={receiptUrl} target="_blank" rel="noreferrer"
                className="text-[12px] text-pb hover:underline flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                Receipt attached
              </a>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Date <span className="text-dr">*</span></label>
              <input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Amount <span className="text-dr">*</span></label>
              <input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                placeholder="0.00" className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Currency</label>
              <select value={form.currency} onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb">
                <option>GBP</option><option>EUR</option><option>USD</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Category <span className="text-dr">*</span></label>
            <select value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb">
              <option value="">Select category</option>
              {expenseCategories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Description <span className="text-dr">*</span></label>
            <input type="text" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="e.g. Team dinner at restaurant" className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
          </div>
          {error && <p className="text-[12px] text-dr">{error}</p>}
          <div className="flex gap-2 flex-wrap">
            <button type="submit" disabled={saving}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
              {saving ? 'Saving…' : 'Log Expense'}
            </button>
            <button type="button" onClick={handleScanClick}
              className="px-4 py-2 text-[13px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 rounded-[8px] transition flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Scan receipt
            </button>
            <button type="button" onClick={() => { setShowForm(false); setForm(emptyExp); setReceiptUrl(''); setError(''); setScanMode(null); }}
              className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]">
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowForm(true)}
            className="text-[13px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-4 py-2 rounded-[8px] transition">
            + Log expense
          </button>
          <button onClick={handleScanClick}
            className="text-[13px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-4 py-2 rounded-[8px] transition flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Scan receipt
          </button>
        </div>
      )}
    </div>
  );
}

// ── Incidents ────────────────────────────────────────────────────────────────

const emptyIncident = { date: '', type: '', description: '', personsInvolved: '', actionTaken: '' };

function IncidentsView({ requestId }) {
  const { currentUser } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(emptyIncident);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'requests', requestId, 'incidents'), orderBy('createdAt', 'desc')),
      (snap) => setIncidents(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [requestId]);

  async function handleAdd(e) {
    e.preventDefault();
    setError('');
    if (!form.date || !form.type || !form.description.trim()) {
      setError('Date, type and description are required.'); return;
    }
    setSaving(true);
    try {
      await addDoc(collection(db, 'requests', requestId, 'incidents'), {
        date:             form.date,
        type:             form.type,
        description:      form.description.trim(),
        personsInvolved:  form.personsInvolved.trim() || null,
        actionTaken:      form.actionTaken.trim() || null,
        reportedBy:       currentUser.email,
        createdAt:        serverTimestamp(),
      });
      setForm(emptyIncident);
      setShowForm(false);
    } catch (err) {
      console.error(err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {incidents.length > 0 && (
        <div className="space-y-3 mb-5">
          {incidents.map((inc) => (
            <div key={inc.id} className="p-4 bg-surface border border-[#E5E7EB] rounded-[10px]">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-[6px] bg-amber-light text-[#92400E]">
                  {inc.type}
                </span>
                <span className="text-[12px] text-[#9CA3AF]">{formatDate(inc.date)}</span>
              </div>
              <p className="text-[13px] text-[#111827] mb-1">{inc.description}</p>
              {inc.personsInvolved && (
                <p className="text-[12px] text-[#6B7280]"><strong>Persons involved:</strong> {inc.personsInvolved}</p>
              )}
              {inc.actionTaken && (
                <p className="text-[12px] text-[#6B7280] mt-0.5"><strong>Action taken:</strong> {inc.actionTaken}</p>
              )}
              <p className="text-[11px] text-[#9CA3AF] mt-2">Reported by {inc.reportedBy}</p>
            </div>
          ))}
        </div>
      )}

      {incidents.length === 0 && !showForm && (
        <p className="text-[13px] text-[#9CA3AF] mb-4">No incidents reported.</p>
      )}

      {showForm ? (
        <form onSubmit={handleAdd} className="border border-[#E5E7EB] rounded-[10px] p-4 space-y-3">
          <p className="text-[12px] font-semibold text-[#374151]">Log incident report</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Date <span className="text-dr">*</span></label>
              <input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Type <span className="text-dr">*</span></label>
              <select value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb">
                <option value="">Select type</option>
                {INCIDENT_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Description <span className="text-dr">*</span></label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Describe what happened…"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb resize-none" />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Persons involved</label>
            <input type="text" value={form.personsInvolved} onChange={(e) => setForm((p) => ({ ...p, personsInvolved: e.target.value }))}
              placeholder="Names of those involved (optional)"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Action taken</label>
            <input type="text" value={form.actionTaken} onChange={(e) => setForm((p) => ({ ...p, actionTaken: e.target.value }))}
              placeholder="What action was taken (optional)"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
          </div>
          {error && <p className="text-[12px] text-dr">{error}</p>}
          <div className="flex gap-2">
            <button type="submit" disabled={saving}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
              {saving ? 'Saving…' : 'Submit Report'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setForm(emptyIncident); setError(''); }}
              className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]">
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button onClick={() => setShowForm(true)}
          className="text-[13px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-4 py-2 rounded-[8px] transition">
          + Log incident
        </button>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

// ── Bottom nav icons ─────────────────────────────────────────────────────────

// ── Checklist (tour leader acknowledgement view) ─────────────────────────────

function ChecklistView({ requestId }) {
  const { currentUser } = useAuth();
  const [checklist, setChecklist]   = useState(null);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'requests', requestId, 'tourChecklist', 'data'),
      (snap) => { setChecklist(snap.exists() ? snap.data() : null); setLoading(false); }
    );
    return unsub;
  }, [requestId]);

  async function acknowledgeItem(sectionIdx, itemId) {
    if (!checklist) return;
    setSaving(true);
    try {
      const sections = checklist.sections.map((sec, si) =>
        si !== sectionIdx ? sec : {
          ...sec,
          items: sec.items.map((item) =>
            item.id !== itemId ? item : { ...item, tourLeaderAcknowledged: !item.tourLeaderAcknowledged }
          ),
        }
      );
      await updateDoc(doc(db, 'requests', requestId, 'tourChecklist', 'data'), {
        sections,
        [`tourLeaderAcknowledgedBy`]: currentUser.email,
      });
    } finally {
      setSaving(false);
    }
  }

  async function acknowledgeConfirmation(idx) {
    if (!checklist) return;
    setSaving(true);
    try {
      const confirmations = checklist.confirmations.map((c, i) =>
        i !== idx ? c : { ...c, tourLeaderAcknowledged: !c.tourLeaderAcknowledged }
      );
      await updateDoc(doc(db, 'requests', requestId, 'tourChecklist', 'data'), { confirmations });
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="text-[13px] text-[#9CA3AF]">Loading…</p>;

  if (!checklist) return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 text-center">
      <p className="text-[14px] font-semibold text-[#111827] mb-1">No checklist yet</p>
      <p className="text-[13px] text-[#9CA3AF]">MST staff will push the operations checklist to you before departure.</p>
    </div>
  );

  const allItemsAcknowledged = (checklist.sections || []).every((sec) =>
    (sec.items || []).every((item) => item.tourLeaderAcknowledged)
  ) && (checklist.confirmations || []).every((c) => c.tourLeaderAcknowledged);

  return (
    <div className="space-y-5">
      {allItemsAcknowledged && (
        <div className="bg-[#F0FDF4] border border-sg/30 rounded-[12px] px-4 py-3 flex items-center gap-2">
          <svg className="w-5 h-5 text-sg flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-[13px] text-sg font-semibold">All items acknowledged — checklist complete.</p>
        </div>
      )}

      {(checklist.sections || []).map((section, si) => (
        <div key={si} className="bg-white rounded-[12px] border border-[#E5E7EB] overflow-hidden">
          <div className="px-4 py-3 bg-surface border-b border-[#E5E7EB]">
            <p className="text-[12px] font-bold text-[#374151] uppercase tracking-[0.9px]">{section.title}</p>
          </div>
          <div className="divide-y divide-[#F3F4F6]">
            {(section.items || []).map((item) => (
              <label key={item.id} className={`flex items-center gap-3 px-4 py-3 cursor-pointer ${saving ? 'opacity-60' : ''}`}>
                <input
                  type="checkbox"
                  checked={!!item.tourLeaderAcknowledged}
                  onChange={() => acknowledgeItem(si, item.id)}
                  disabled={saving}
                  className="w-5 h-5 rounded accent-pb flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className={`text-[14px] ${item.tourLeaderAcknowledged ? 'line-through text-[#9CA3AF]' : 'text-[#111827] font-medium'}`}>{item.name}</p>
                  {item.adminQty && (
                    <p className="text-[11px] text-[#9CA3AF] mt-0.5">Required: {item.adminQty}</p>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>
      ))}

      {(checklist.confirmations || []).length > 0 && (
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] overflow-hidden">
          <div className="px-4 py-3 bg-surface border-b border-[#E5E7EB]">
            <p className="text-[12px] font-bold text-[#374151] uppercase tracking-[0.9px]">Confirmation</p>
          </div>
          <div className="divide-y divide-[#F3F4F6]">
            {checklist.confirmations.map((c, i) => (
              <label key={i} className={`flex items-center gap-3 px-4 py-3 cursor-pointer ${saving ? 'opacity-60' : ''}`}>
                <input
                  type="checkbox"
                  checked={!!c.tourLeaderAcknowledged}
                  onChange={() => acknowledgeConfirmation(i)}
                  disabled={saving}
                  className="w-5 h-5 rounded accent-pb flex-shrink-0"
                />
                <p className={`text-[14px] flex-1 ${c.tourLeaderAcknowledged ? 'line-through text-[#9CA3AF]' : 'text-[#111827] font-medium'}`}>{c.text}</p>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Register view ─────────────────────────────────────────────────────────────

function RegisterView({ requestId }) {
  const { currentUser } = useAuth();
  const [participants,  setParticipants]  = useState([]);
  const [registers,     setRegisters]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showForm,      setShowForm]      = useState(false);
  const [description,   setDescription]  = useState('');
  const [attendance,    setAttendance]    = useState({});
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState('');

  useEffect(() => {
    getDocs(query(collection(db, 'requests', requestId, 'participants'), orderBy('submittedAt'))).then((snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setParticipants(list);
      const initial = {};
      list.forEach((p) => { initial[p.id] = true; });
      setAttendance(initial);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [requestId]);

  useEffect(() => {
    const q = query(collection(db, 'requests', requestId, 'registers'), orderBy('takenAt', 'desc'));
    return onSnapshot(q, (snap) => {
      setRegisters(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [requestId]);

  function toggleAll(present) {
    const next = {};
    participants.forEach((p) => { next[p.id] = present; });
    setAttendance(next);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!description.trim()) { setError('Please enter a description.'); return; }
    setSaving(true);
    setError('');
    try {
      const totalPresent = Object.values(attendance).filter(Boolean).length;
      const attendanceDetails = participants.map((p) => ({
        id: p.id,
        name: [p.firstName, p.surname].filter(Boolean).join(' ') || 'Unknown',
        travellerType: p.travellerType || '',
        present: attendance[p.id] ?? true,
      }));
      await addDoc(collection(db, 'requests', requestId, 'registers'), {
        description: description.trim(),
        takenAt: serverTimestamp(),
        takenBy: currentUser?.email || 'tour-leader',
        attendance,
        attendanceDetails,
        totalPresent,
        totalParticipants: participants.length,
      });
      setShowForm(false);
      setDescription('');
      const reset = {};
      participants.forEach((p) => { reset[p.id] = true; });
      setAttendance(reset);
    } catch (err) {
      setError(err.message || 'Failed to submit register.');
    } finally {
      setSaving(false);
    }
  }

  function fmtTs(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  if (loading) return <p className="text-[13px] text-[#9CA3AF] py-6 text-center">Loading…</p>;

  if (showForm) {
    return (
      <div className="space-y-4 pb-24">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => setShowForm(false)} className="text-[#9CA3AF] hover:text-[#374151] text-[13px]">← Back</button>
          <h2 className="text-[15px] font-bold text-navy">Take Register</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">
              When is this register for? *
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Getting off the bus at the match"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
              autoFocus
            />
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-[12px] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <p className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider">
                {Object.values(attendance).filter(Boolean).length}/{participants.length} present
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={() => toggleAll(true)}
                  className="text-[11px] text-pb hover:text-navy font-semibold">All present</button>
                <button type="button" onClick={() => toggleAll(false)}
                  className="text-[11px] text-[#6B7280] hover:text-[#374151] font-semibold">All absent</button>
              </div>
            </div>
            {participants.length === 0 ? (
              <p className="px-4 py-4 text-[13px] text-[#9CA3AF]">No participants have registered yet.</p>
            ) : (
              <div className="divide-y divide-[#F3F4F6] max-h-[50vh] overflow-y-auto">
                {participants.map((p) => (
                  <label key={p.id} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#F9FAFB] transition">
                    <input
                      type="checkbox"
                      checked={attendance[p.id] ?? true}
                      onChange={(e) => setAttendance((prev) => ({ ...prev, [p.id]: e.target.checked }))}
                      className="w-5 h-5 rounded border-[#D1D5DB] text-sg focus:ring-sg shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-[#111827] truncate">
                        {[p.firstName, p.surname].filter(Boolean).join(' ') || 'Unknown'}
                      </p>
                      <p className="text-[11px] text-[#9CA3AF] truncate">{p.travellerType || ''}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                      (attendance[p.id] ?? true) ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'
                    }`}>
                      {(attendance[p.id] ?? true) ? 'Present' : 'Absent'}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {error && <p className="text-[12px] text-red-600">{error}</p>}

          <div className="flex gap-2">
            <button type="submit" disabled={saving}
              className="flex-1 py-3 bg-pb hover:bg-navy text-white text-[13px] font-bold rounded-[10px] transition disabled:opacity-60">
              {saving ? 'Submitting…' : 'Submit Register'}
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="px-5 text-[13px] text-[#6B7280] hover:text-[#374151]">
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-bold text-navy">Registers</h2>
          <p className="text-[12px] text-[#9CA3AF] mt-0.5">{registers.length} submitted</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2.5 bg-pb hover:bg-navy text-white text-[12px] font-bold rounded-[10px] transition"
        >
          + Take Register
        </button>
      </div>

      {registers.length === 0 ? (
        <div className="text-center py-10 text-[#9CA3AF]">
          <p className="text-[13px]">No registers taken yet.</p>
          <p className="text-[12px] mt-1">Tap "Take Register" to record attendance.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {registers.map((reg) => {
            const absent = participants.filter((p) => reg.attendance && reg.attendance[p.id] === false);
            return (
              <div key={reg.id} className="bg-white border border-[#E5E7EB] rounded-[12px] overflow-hidden">
                <div className="px-4 py-3 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-[#111827]">{reg.description}</p>
                    <p className="text-[11px] text-[#9CA3AF] mt-0.5">{fmtTs(reg.takenAt)}</p>
                  </div>
                  <span className={`shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full ${
                    reg.totalPresent === reg.totalParticipants ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {reg.totalPresent ?? '?'}/{reg.totalParticipants ?? participants.length} present
                  </span>
                </div>
                {absent.length > 0 && (
                  <div className="px-4 pb-3 border-t border-[#F3F4F6] pt-2">
                    <p className="text-[10px] font-bold text-red-500 uppercase tracking-wider mb-1">Absent</p>
                    <p className="text-[12px] text-[#374151]">
                      {absent.map((p) => [p.firstName, p.surname].filter(Boolean).join(' ')).join(', ')}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NavIconTeamInfo() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="23" y1="11" x2="17" y2="11"/><line x1="20" y1="8" x2="20" y2="14"/></svg>;
}

function NavIconChecklist() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1" ry="1"/><path d="M9 12l2 2 4-4"/></svg>;
}

function NavIconResources() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/></svg>;
}

function NavIconTour() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
}
function NavIconItinerary() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="13" y2="18"/></svg>;
}
function NavIconParticipants() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
}
function NavIconExpenses() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>;
}
function NavIconIncidents() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
}
function NavIconRooming() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8"/><path d="M2 14h20"/><path d="M7 14v6"/><path d="M17 14v6"/><path d="M2 20h20"/><path d="M6 10V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4"/></svg>;
}
function NavIconTeams() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
}
function NavIconFoodOrders() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 2v7c0 1.1.9 2 2 2s2-.9 2-2V2"/>
      <path d="M7 2v20"/>
      <path d="M21 15V2c-2.8 0-5 2.2-5 5v6c0 1.1.9 2 2 2h3z"/>
      <path d="M21 15v7"/>
    </svg>
  );
}
function NavIconRegister() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>;
}
function NavIconSocial() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>;
}
function NavIconMore() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/>
      <rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/>
      <rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  );
}

const TAB_ICONS = {
  tour:         <NavIconTour />,
  itinerary:    <NavIconItinerary />,
  team_info:    <NavIconTeamInfo />,
  participants: <NavIconParticipants />,
  register:     <NavIconRegister />,
  social:       <NavIconSocial />,
  checklist:    <NavIconChecklist />,
  expenses:     <NavIconExpenses />,
  rooming:      <NavIconRooming />,
  teams:        <NavIconTeams />,
  incidents:    <NavIconIncidents />,
  food:         <NavIconFoodOrders />,
  resources:    <NavIconResources />,
};

// ── Main component ────────────────────────────────────────────────────────────

export default function TourLeaderTourDetail({ id: idProp } = {}) {
  const params = useParams();
  const id = idProp || params.id;
  const { currentUser } = useAuth();
  const { stagesFor } = useStatusPipeline();
  const navigate = usePreviewNavigate();
  const [request, setRequest]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('tour');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [teamInfo, setTeamInfo] = useState(null);
  const [teamInfoLoading, setTeamInfoLoading] = useState(false);
  const [editingTeamInfo, setEditingTeamInfo] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'requests', id), (snap) => {
      if (snap.exists()) setRequest({ id: snap.id, ...snap.data() });
      setLoading(false);
    });
    return unsub;
  }, [id]);

  useEffect(() => {
    if (activeTab !== 'team_info') return;
    setTeamInfoLoading(true);
    import('firebase/firestore').then(({ getDoc, doc: fsDoc }) => {
      getDoc(fsDoc(db, 'requests', id, 'teamInfo', 'data'))
        .then((snap) => { if (snap.exists()) setTeamInfo({ id: snap.id, ...snap.data() }); })
        .catch(() => {})
        .finally(() => setTeamInfoLoading(false));
    });
  }, [activeTab, id]);

  async function handleStageToggle(stageKey) {
    if (updating) return;
    setUpdating(true);
    const current = !!request.stageProgress?.[stageKey];
    try {
      await updateDoc(doc(db, 'requests', id), { [`stageProgress.${stageKey}`]: !current });
    } finally {
      setUpdating(false);
    }
  }

  async function handleAdvanceToPostTour() {
    if (updating) return;
    if (!isCategoryComplete('leader_signoff', request.stageProgress, stagesFor('leader_signoff', request))) return;
    setUpdating(true);
    const updates = { category: 'post_tour' };
    if (CATEGORY_STATUS_SYNC['post_tour']) updates.status = CATEGORY_STATUS_SYNC['post_tour'];
    try {
      await updateDoc(doc(db, 'requests', id), updates);
    } finally {
      setUpdating(false);
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F9FAFB]">
      <p className="text-[14px] text-[#9CA3AF]">Loading…</p>
    </div>
  );
  if (!request) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F9FAFB]">
      <p className="text-[14px] text-[#9CA3AF]">Tour not found.</p>
    </div>
  );

  const cat = request.category || 'sales_admin';
  const stageProgress = request.stageProgress || {};
  const leaderSignoffStages = stagesFor('leader_signoff', request);
  const leaderTasksComplete = isCategoryComplete('leader_signoff', stageProgress, leaderSignoffStages);

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-[calc(80px+env(safe-area-inset-bottom,0px))] overflow-x-hidden">

      {/* Compact sticky header with breadcrumb */}
      <div className="sticky top-0 z-30 bg-white border-b border-[#E5E7EB] px-4 pt-2 pb-3">
        {/* Breadcrumb row */}
        <nav className="flex items-center gap-1 text-[11px] mb-1.5" aria-label="Breadcrumb">
          <button
            onClick={() => navigate('/tour-leader')}
            className="text-[#9CA3AF] hover:text-[#6B7280] transition flex items-center gap-1"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            My Tours
          </button>
          <svg className="w-2.5 h-2.5 text-[#D1D5DB]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-[#374151] font-medium truncate">
            {request.tourName || `${request.sport} — ${request.destination}`}
          </span>
        </nav>
        {/* Tour name + status row */}
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-[15px] font-bold text-[#111827] truncate">
              {request.tourName || `${request.sport} — ${request.destination}`}
            </h2>
            <p className="text-[12px] text-[#9CA3AF] truncate">
              {formatDate(request.departureDate)} – {formatDate(request.returnDate)}
              {totalTravellers(request) ? ` · ${totalTravellers(request)} pax` : ''}
            </p>
          </div>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap shrink-0 ${CATEGORY_STYLES[cat] || 'bg-[#F3F4F6] text-[#374151]'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${CATEGORY_DOT[cat] || 'bg-[#9CA3AF]'}`} />
            {CATEGORY_LABELS[cat] || cat}
          </span>
        </div>
      </div>

      {/* Tab content */}
      <div className="px-4 py-5 max-w-3xl mx-auto">

        {/* Tour Info */}
        {activeTab === 'tour' && (
          <div className="space-y-5">
            <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-4 space-y-5">
              <div>
                <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-[0.9px] mb-3">Tour Details</p>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Sport"        value={request.sport} />
                  <Field label="Destination"  value={request.destination} />
                  <Field label="Departure"    value={formatDate(request.departureDate)} />
                  <Field label="Return"       value={formatDate(request.returnDate)} />
                  <Field label="Group type"   value={request.groupType} />
                  <Field label="Total travellers" value={totalTravellers(request) ? `${totalTravellers(request)} pax` : null} />
                  <Field label="Players"      value={request.participants ? `${request.participants}` : null} />
                  {request.participantsMale != null && <Field label="Males"          value={`${request.participantsMale}`} />}
                  {request.participantsFemale != null && <Field label="Females"        value={`${request.participantsFemale}`} />}
                  {request.numberOfSquads != null   && <Field label="Teams / Squads" value={`${request.numberOfSquads}`} />}
                </div>
              </div>
              {request.flightDetails && (request.flightDetails.airline || request.flightDetails.outboundFlight || request.flightDetails.returnFlight) && (
                <div className="border-t border-[#F3F4F6] pt-4">
                  <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-[0.9px] mb-3">Flight Details</p>
                  <div className="grid grid-cols-2 gap-4">
                    {request.flightDetails.airline        && <Field label="Airline"             value={request.flightDetails.airline} />}
                    {request.flightDetails.outboundFlight && <Field label="Outbound flight"     value={request.flightDetails.outboundFlight} />}
                    {request.flightDetails.outboundTime   && <Field label="Outbound departure"  value={request.flightDetails.outboundTime} />}
                    {request.flightDetails.arrivalTime    && <Field label="Arrival time"        value={request.flightDetails.arrivalTime} />}
                    {request.flightDetails.returnFlight        && <Field label="Return flight"       value={request.flightDetails.returnFlight} />}
                    {request.flightDetails.returnTime          && <Field label="Return departure"    value={request.flightDetails.returnTime} />}
                    {request.flightDetails.returnArrivalTime   && <Field label="Return arrival"      value={request.flightDetails.returnArrivalTime} />}
                  </div>
                </div>
              )}
              <div className="border-t border-[#F3F4F6] pt-4">
                <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-[0.9px] mb-3">Contact</p>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Contact name" value={request.contactName} />
                  <Field label="Phone"        value={request.phone} />
                  <Field label="Email"        value={request.customerEmail} />
                </div>
              </div>
              {request.notes && (
                <div className="border-t border-[#F3F4F6] pt-4">
                  <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-[0.9px] mb-2">Notes</p>
                  <p className="text-[13px] text-[#374151] whitespace-pre-wrap leading-relaxed">{request.notes}</p>
                </div>
              )}
            </div>

            {/* Staff notes pinned to this tour — read-only for tour leaders */}
            <TourNotes request={request} legacyNotes={request.internalNotes || ''} readOnly />

            {/* Tour Leader Tasks — visible only when the tour is in the leader_signoff stage */}
          {cat === 'leader_signoff' && (
            <div className="bg-white rounded-[12px] border border-sg/40 overflow-hidden">
              <div className="px-4 py-3 bg-green-light/60 border-b border-sg/20">
                <p className="text-[11px] font-bold text-sg uppercase tracking-[0.9px]">Your Tasks</p>
                <p className="text-[12px] text-sg/80 mt-0.5">Complete all tasks below before advancing.</p>
              </div>
              <div className="px-4 py-4 space-y-3">
                {leaderSignoffStages.map((stage) => {
                  const ticked = !!stageProgress[stage.key];
                  return (
                    <label
                      key={stage.key}
                      className={`flex items-center gap-3 cursor-pointer ${updating ? 'opacity-60' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={ticked}
                        onChange={() => handleStageToggle(stage.key)}
                        disabled={updating}
                        className="w-5 h-5 rounded accent-[#16A34A] flex-shrink-0"
                      />
                      <span className={`text-[14px] ${ticked ? 'line-through text-[#9CA3AF]' : 'text-[#111827] font-medium'}`}>
                        {stage.label}
                      </span>
                    </label>
                  );
                })}
              </div>
              <div className="px-4 pb-4">
                {leaderTasksComplete ? (
                  <button
                    onClick={handleAdvanceToPostTour}
                    disabled={updating}
                    className="w-full py-3 bg-sg hover:bg-[#15803D] text-white text-[13px] font-semibold rounded-[10px] transition disabled:opacity-60"
                  >
                    Advance to Post Tour →
                  </button>
                ) : (
                  <p className="text-[12px] text-[#9CA3AF] text-center py-1">Complete all tasks to advance</p>
                )}
              </div>
            </div>
          )}

          {/* Past stages complete / handed back notice */}
          {cat === 'post_tour' && (
            <div className="bg-[#F0FDF4] border border-sg/30 rounded-[12px] px-4 py-3">
              <p className="text-[13px] text-sg font-semibold">All tour leader tasks completed.</p>
              <p className="text-[12px] text-sg/80 mt-0.5">The tour has moved to post-tour — MST will handle the remaining steps.</p>
            </div>
          )}
        </div>
        )}

        {activeTab === 'itinerary'    && <FullItineraryBuilder requestId={id} request={request} readOnly canUploadTickets showFixtureContacts myLeaderId={currentUser?.uid} />}
        {activeTab === 'team_info' && (
          <div className="space-y-4">
            {teamInfoLoading ? (
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-10 text-center">
                <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
              </div>
            ) : editingTeamInfo && teamInfo ? (
              <TeamInfoEditPanel
                requestId={id}
                teamInfo={teamInfo}
                onSave={(updated) => { setTeamInfo(updated); setEditingTeamInfo(false); }}
                onCancel={() => setEditingTeamInfo(false)}
              />
            ) : !teamInfo ? (
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-10 text-center">
                <p className="text-[14px] font-bold text-[#111827] mb-2">No team info submitted yet</p>
                <p className="text-[13px] text-[#6B7280] mb-4">You can add team information directly from here.</p>
                <button
                  onClick={() => { setTeamInfo({}); setEditingTeamInfo(true); }}
                  className="px-4 py-2 bg-pb text-white text-[12px] font-semibold rounded-[8px] hover:bg-navy transition"
                >
                  Add Team Info
                </button>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
                  <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
                    <h3 className="text-[14.5px] font-bold text-[#111827]">Coach & Team Information</h3>
                    <button
                      onClick={() => setEditingTeamInfo(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-pb border border-pb/30 rounded-[8px] hover:bg-pb/5 transition"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      Edit
                    </button>
                  </div>
                  <div className="px-[18px] py-[16px]">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                      {[
                        ['Squads', teamInfo.numSquads],
                        teamInfo.goalkeepersTotal != null ? ['Total Goalkeepers', teamInfo.goalkeepersTotal] : null,
                      ].filter(Boolean).map(([label, val]) => (
                        <div key={label}>
                          <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[3px]">{label}</p>
                          <p className="text-[13.5px] font-semibold text-[#111827]">{val ?? '—'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {(teamInfo.squads || []).map((sq, i) => (
                  <SquadCard key={i} squad={sq} index={i} request={request} uidPrefix="tl-kit" />
                ))}
              </>
            )}
            {!editingTeamInfo && (
              <TeamFixturesSection requestId={id} request={request} canEdit myLeaderId={currentUser?.uid} />
            )}
          </div>
        )}
        {activeTab === 'participants' && <ParticipantsView requestId={id} />}
        {activeTab === 'register'     && <RegisterView requestId={id} />}
        {activeTab === 'social'       && (
          <div className="space-y-4">
            <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-5">
              <h3 className="text-[14px] font-bold text-[#111827] mb-1">Social Media Plan</h3>
              <p className="text-[12px] text-[#9CA3AF] mb-4">What marketing wants captured on this tour.</p>
              <SocialMediaPlan requestId={id} role="tour_leader" />
            </div>

            {request?.brand === 'padelante' && (
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-5">
                <h3 className="text-[14px] font-bold text-[#111827] mb-1">Game Content Checklist</h3>
                <p className="text-[12px] text-[#9CA3AF] mb-4">Tick each item off as you capture it on game day.</p>
                <GameContentChecklist requestId={id} />
              </div>
            )}
          </div>
        )}
        {activeTab === 'checklist'    && <ChecklistView requestId={id} />}
        {activeTab === 'expenses'     && <ExpensesView requestId={id} request={request} />}
        {activeTab === 'rooming'      && <StaffRoomingTab requestId={id} request={request} />}
        {activeTab === 'teams'        && <TeamsTab requestId={id} request={request} mode="coach" myLeaderId={currentUser?.uid} />}
        {activeTab === 'incidents'    && <IncidentsView requestId={id} />}
        {activeTab === 'food'         && <FoodOrders requestId={id} request={request} />}
        {activeTab === 'resources'    && <ResourcePack requestId={id} request={request} readOnly />}
      </div>

      {/* Fixed bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E7EB] flex pb-safe">
        {PRIMARY_TABS.map((tab) => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setShowMoreMenu(false); }}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-3 min-h-[56px] transition-colors ${active ? 'text-pb' : 'text-[#9CA3AF] hover:text-[#6B7280]'}`}
            >
              <span className={`transition-colors ${active ? 'text-pb' : 'text-[#9CA3AF]'}`}>
                {TAB_ICONS[tab.key]}
              </span>
              <span className={`text-[10px] font-semibold tracking-wide ${active ? 'text-pb' : 'text-[#9CA3AF]'}`}>
                {tab.label}
              </span>
            </button>
          );
        })}
        <button
          onClick={() => setShowMoreMenu((s) => !s)}
          className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-3 min-h-[56px] transition-colors ${MORE_TABS.some((t) => t.key === activeTab) || showMoreMenu ? 'text-pb' : 'text-[#9CA3AF] hover:text-[#6B7280]'}`}
        >
          <NavIconMore />
          <span className={`text-[10px] font-semibold tracking-wide ${MORE_TABS.some((t) => t.key === activeTab) || showMoreMenu ? 'text-pb' : 'text-[#9CA3AF]'}`}>
            More
          </span>
        </button>
      </nav>

      {/* More menu overlay */}
      {showMoreMenu && (
        <>
          <div
            data-preview-nav
            className="fixed inset-0 z-30 bg-black/40"
            onClick={() => setShowMoreMenu(false)}
          />
          <div data-preview-nav className="fixed bottom-[calc(64px+env(safe-area-inset-bottom,0px))] left-0 right-0 z-40 bg-white rounded-t-[20px] border-t border-[#E5E7EB] shadow-2xl px-4 pt-4 pb-6">
            <div className="w-10 h-1 bg-[#E5E7EB] rounded-full mx-auto mb-4" />
            <div className="grid grid-cols-2 gap-3">
              {MORE_TABS.map((tab) => {
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => { setActiveTab(tab.key); setShowMoreMenu(false); }}
                    className={`flex flex-col items-center gap-2 py-4 px-3 rounded-[14px] border transition-all ${active ? 'border-pb bg-pb/5 text-pb' : 'border-[#E5E7EB] text-[#6B7280] hover:border-pb/40 hover:bg-[#F9FAFB]'}`}
                  >
                    <span>{TAB_ICONS[tab.key]}</span>
                    <span className="text-[13px] font-semibold">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
